// src/services/cashFlowService.js
import { supabase } from '../lib/supabase';

// --- helpers ---
const toDateOnly = (value) => {
  // aceita 'YYYY-MM-DD' ou Date
  if (!value) return null;

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString().slice(0, 10);
  }

  if (typeof value === 'string') {
    // já está no formato YYYY-MM-DD?
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;

    const d = new Date(value);
    if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  }

  return null;
};

const safeResult = (data, fallback = []) => (Array.isArray(data) ? data : fallback);

const swallowSchemaErrors = (error) => {
  // 42P01 = relation does not exist
  // 42703 = column does not exist
  // 22007 = invalid datetime format (ex: "undefined")
  const code = error?.code;
  return code === '42P01' || code === '42703' || code === '22007';
};

// --- service ---
export const cashFlowService = {
  /**
   * Busca saldos bancários (tabela: bank_balances)
   * Importante: sua coluna é balance_date (não "date")
   */
  async getBankBalances(date) {
    const dateOnly = toDateOnly(date) || toDateOnly(new Date());
    if (!dateOnly) return [];

    try {
      // Se quiser “saldo mais recente até a data”, dá para ajustar depois.
      const { data, error } = await supabase
        .from('bank_balances')
        .select('*')
        .order('balance_date', { ascending: false });

      if (error) throw error;
      return safeResult(data, []);
    } catch (err) {
      if (swallowSchemaErrors(err)) return [];
      throw err;
    }
  },

  /**
   * Recebíveis (tabela: receivables)
   * Como não temos certeza das colunas, fazemos select simples e ordenação.
   * Depois refinamos por período quando você me mandar o schema/colunas.
   */
  async getReceivables(date) {
    const dateOnly = toDateOnly(date) || toDateOnly(new Date());
    if (!dateOnly) return [];

    try {
      const { data, error } = await supabase
        .from('receivables')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return safeResult(data, []);
    } catch (err) {
      if (swallowSchemaErrors(err)) return [];
      throw err;
    }
  },

  /**
   * Despesas (tabela: expenses)
   */
  async getExpenses(date) {
    const dateOnly = toDateOnly(date) || toDateOnly(new Date());
    if (!dateOnly) return [];

    try {
      const { data, error } = await supabase
        .from('expenses')
        .select('*')
        .order('expense_date', { ascending: false });

      if (error) throw error;
      return safeResult(data, []);
    } catch (err) {
      if (swallowSchemaErrors(err)) return [];
      throw err;
    }
  },

  /**
   * Se sua tela pedir cash_flow direto
   */
  async getCashFlow(date) {
    const dateOnly = toDateOnly(date) || toDateOnly(new Date());
    if (!dateOnly) return [];

    try {
      const { data, error } = await supabase
        .from('cash_flow')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return safeResult(data, []);
    } catch (err) {
      if (swallowSchemaErrors(err)) return [];
      throw err;
    }
  }
};

export default cashFlowService;
